/* eslint-disable max-lines */
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Progress,
  CardHeader,
  CardBody,
  Modal,
  Spinner,
  FormGroup,
  InputGroup,
  Input,
  InputGroupAddon,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useState, useEffect, useMemo } from 'react';
import { buildPublicEnrollmentPayload } from './publicEnrollmentPayload';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import EnrollPaymentForm from './EnrollPaymentForm';
import { StripeOutcome, requiresRegistrationPayment } from '../../../helpers/stripeStatus';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { useParams } from 'react-router-dom';
import SchoolService from '../../../services/School';
import WaitingListService from '../../../services/WaitingListPublic';
import PromoCodeService from '../../../services/PromoCodePublic';
import TransactionService from '../../../services/Transaction';
import UserService from '../../../services/User';
import ParentForm from './ParentForm';
import ChildrenForm from './ChildrenForm';
import { toLocaleFormatSlash } from '../../../helpers/dateFormat';
import SuccessPage from './SuccessPage';
import { toast } from 'react-toastify';

const EnrollForm = () => {
  const { schoolUri } = useParams<{ schoolUri: string }>();
  const secret = new URLSearchParams(window.location.search).get('secret');
  const code = new URLSearchParams(window.location.search).get('code');

  const progressBar = [
    { step: 1, percent: 15 },
    { step: 2, percent: 35 },
    { step: 3, percent: 70 },
    { step: 4, percent: 100 },
  ];

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS || '',
  });
  const center = useMemo(() => ({ lat: 28.47445499709106, lng: -81.27141287241294 }), []);

  const styles = {
    p: {
      fontSize: '14px',
      lineHeight: 1,
    },
    bold: {
      fontWeight: '600',
    },
  };
  const [parents, setParents] = useState([
    {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  ] as {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  }[]);

  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [formMessage, setFormMessage] = useState('');
  const [franchiseePicture, setFranchiseePicture] = useState('');
  const [classId, setClassId] = useState('');
  const [classTypeName, setClassTypeName] = useState('');
  const [priceSheet, setPriceSheet] = useState(null as any);
  const [paymentFrequency, setPaymentFrequency] = useState('');
  const [school, setSchool] = useState(null as any);
  const [step, setStep] = useState(1);
  const [franchiseeName, setFranchiseeName] = useState('');
  const [modalCard, setModalCard] = useState(false);

  const [childId, setChildId] = useState('');
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [childBirthday, setChildBirthday] = useState('');
  const [childStartDate, setChildStartDate] = useState('');
  const [childClassType, setChildClassType] = useState('');

  const [registrationFee, setRegistrationFee] = useState(0);

  const [stripePK, setStripePK] = useState('');
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [pendingTransactionId, setPendingTransactionId] = useState('');
  const [paymentKidsId, setPaymentKidsId] = useState('');
  const [paymentSession, setPaymentSession] = useState('');
  const [paymentError, setPaymentError] = useState('');

  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState(false);
  const [promoCodeObj, setPromoCodeObj] = useState({} as any);

  const [lastTransaction, setLastTransaction] = useState({} as any);
  const [enrollmentDone, setEnrollmentDone] = useState(false);
  const [enrollmentToken, setEnrollmentToken] = useState('');

  const setParentID = (index: number, value: string) => {
    const newParents = [...parents];
    newParents.forEach((k, i) => {
      if (i == index) {
        k.id = value;
      }
    });
    setParents(newParents);
  };

  const setParentFirstName = (index: number, value: string) => {
    const newParents = [...parents];
    newParents.forEach((k, i) => {
      if (i == index) {
        k.firstName = value;
      }
    });
    setParents(newParents);
  };

  const setParentLastName = (index: number, value: string) => {
    const newParents = [...parents];
    newParents.forEach((k, i) => {
      if (i == index) {
        k.lastName = value;
      }
    });
    setParents(newParents);
  };

  const setParentEmail = (index: number, value: string) => {
    const newParents = [...parents];
    newParents.forEach((k, i) => {
      if (i == index && !k.id) {
        k.email = value;
      }
    });
    setParents(newParents);
  };

  const setParentPhone = (index: number, value: string) => {
    const newParents = [...parents];
    newParents.forEach((k, i) => {
      if (i == index) {
        k.phone = value;
      }
    });
    setParents(newParents);
  };

  const addParent = () => {
    const newParent = [...parents];
    newParent.push({
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });
    setParents(newParent);
  };

  const deleteParent = (index: number) => {
    const newParents = parents.filter((_, i) => index !== i);
    setParents(newParents);
  };

  const applyPromoCode = async () => {
    if (!promoCode) return;
    try {
      const result = await PromoCodeService().getCode(promoCode, school?.id);
      const codeResult = result?.data || {};
      if (!codeResult?.id) {
        setPromoCodeError(true);
        setPromoCodeApplied(false);
        return;
      }
      setPromoCodeError(false);
      setPromoCodeApplied(true);
      setPromoCodeObj(codeResult);
    } catch (e) {
      console.error('Error on get promo code');
    }
  };

  const removePromoCode = async () => {
    setPromoCodeError(false);
    setPromoCodeApplied(false);
    setPromoCodeObj({});
  };

  const createOrUpdatePublic = async () => {
    try {
      let firstParent;
      let firstKidsId;
      let session = enrollmentToken;
      for (let i = 0; i <= parents?.length; i++) {
        const p = parents[i];
        if (!p?.firstName || !p?.email) continue;
        const data = buildPublicEnrollmentPayload(p, i === 0, {
          schoolId: school?.id,
          enrollmentToken: session,
          code,
          child: {
            id: childId,
            firstName: childFirstName,
            lastName: childLastName,
            birthday: childBirthday,
            startDate: childStartDate,
            classTypeId: childClassType,
            classId,
            promoCodeId: promoCodeObj?.id,
          },
          firstKidsId,
        });
        const result: any = p?.id
          ? await UserService('').updatePublic({ id: p?.id, ...data })
          : await UserService('').createPublic(data);
        const parent: any = result?.data || {};
        session = parent?.enrollmentToken || session;
        setEnrollmentToken(session);
        if (i === 0) {
          firstParent = parent;
          firstKidsId = parent?.kidsId || '';
          if (firstKidsId) setChildId(firstKidsId);
        }
        setParentID(i, parent?.id);
      }

      return { parent: firstParent, session };
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Save error.');
      console.error('Error on save parent');
    }
  };

  const startPayment = async () => {
    setLoading(true);
    setPaymentError('');
    const saved = await createOrUpdatePublic();
    const p: any = saved?.parent;
    const userId = p?.id || '';
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      const intent = await TransactionService('').createPublicIntent({
        userId,
        kidsId: p?.kidsId || '',
        transactionType: 'CREDIT_CARD',
        description: '',
        email: parents?.[0]?.email || '',
        enrollmentToken: saved?.session || '',
      });
      const secret = intent?.data?.clientSecret || '';
      if (!secret) {
        throw new Error('missing client secret');
      }
      setClientSecret(secret);
      setPendingTransactionId(intent?.data?.id || '');
      setPaymentKidsId(p?.kidsId || '');
      setPaymentSession(saved?.session || '');
      setLoading(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'We could not start this payment. Please try again.');
      setLoading(false);
    }
  };

  const handlePaymentSettled = async (outcome: StripeOutcome) => {
    setLoading(true);
    setModalCard(false);
    try {
      const synced = await TransactionService('').syncPublicIntent({
        transactionId: pendingTransactionId,
        kidsId: paymentKidsId,
        enrollmentToken: paymentSession,
      });
      setLastTransaction(synced?.data || { id: pendingTransactionId });
    } catch (err: any) {
      setLastTransaction({ id: pendingTransactionId });
    }
    if (outcome.status !== 'succeeded') {
      toast.info(outcome.message);
    }
    setClientSecret('');
    setLoading(false);
  };

  const finishWithoutPayment = async () => {
    setLoading(true);
    const saved = await createOrUpdatePublic();
    if (saved?.parent?.id) {
      setEnrollmentDone(true);
    }
    setLoading(false);
  };

  const cancelPayment = () => {
    setModalCard(false);
    setClientSecret('');
    setPaymentError('');
  };

  useEffect(() => {
    setLoading(false);
  }, [lastTransaction]);

  useEffect(() => {
    const baseFee = Number(school?.registrationFee || 0);
    const discountPercent = Number(promoCodeObj?.regFee || 0);
    const discount = discountPercent > 0 ? Number(((baseFee * discountPercent) / 100).toFixed(2)) : 0;
    setRegistrationFee(Number((baseFee - discount).toFixed(2)));
  }, [promoCodeObj, school]);

  useEffect(() => {
    if (priceSheet?.paymentFrequency) {
      const s = String(priceSheet.paymentFrequency).toLowerCase();
      setPaymentFrequency(s[0].toUpperCase() + s.slice(1));
    }
  }, [priceSheet]);

  useEffect(() => {
    if (step === 4) {
      if (requiresRegistrationPayment(registrationFee)) {
        setModalCard(true);
      } else {
        finishWithoutPayment();
      }
      setStep(3);
    }
  }, [step]);

  useEffect(() => {
    const getSchool = async () => {
      setLoading(true);
      try {
        const result = await SchoolService('').getSchoolPublic(schoolUri, code);
        const currentSchool = result?.data || {};
        if (!currentSchool || (currentSchool && !currentSchool.onlineEnrollment && !secret && !code)) {
          setShowForm(false);
          setFormMessage('Invalid school.');
          return;
        }
        const r = await SchoolService('').getSchoolStripeCurrent(currentSchool?.id);
        if (r?.data?.stripePublicKey) {
          setStripePK(r?.data?.stripePublicKey || '');
          setStripePromise(loadStripe(r?.data?.stripePublicKey, { locale: 'en' }));
        }
        setSchool(currentSchool);
        setFranchiseeName(currentSchool?.franchisee?.name || '');
        setFranchiseePicture(currentSchool?.franchisee?.picture || '');
        setRegistrationFee(currentSchool?.registrationFee || 0);

        if (!secret && code && !currentSchool.enrollCodeValid && !currentSchool.onlineEnrollment) {
          setShowForm(false);
          setFormMessage('Invalid school.');
          return;
        }
        if (secret) {
          let data: any = {};
          try {
            const result = await WaitingListService('').getBySecret(secret);
            data = result?.data || {};
          } catch (secretError) {
            data = {};
          }
          if (!data.id) {
            if (!currentSchool.onlineEnrollment) {
              setShowForm(false);
              setFormMessage('Invalid or expired link.');
            }
            return;
          }
          setParents((current) => {
            const next = [...current];
            next[0] = {
              ...next[0],
              id: data?.user?.id || '',
              firstName: data?.user?.firstName || '',
              lastName: data?.user?.lastName || '',
              email: data?.user?.email || '',
              phone: data?.user?.phone || '',
            };
            return next;
          });
          setEnrollmentToken(data?.enrollmentToken || '');
          setChildId(data?.kidsList?.[0]?.id || '');
          setChildFirstName(data?.kidsList?.[0]?.firstName || '');
          setChildLastName(data?.kidsList?.[0]?.lastName || '');
          setChildBirthday(data?.kidsList?.[0]?.birthday ? toLocaleFormatSlash(data?.kidsList?.[0]?.birthday) : '');
        }
      } catch (e) {
        console.error('Error searching for School');
      } finally {
        setLoading(false);
      }
    };
    getSchool();
  }, [schoolUri, code, secret]);

  return (
    <>
      {!lastTransaction?.id && !enrollmentDone ? (
        <>
          <Container fluid>
            <Row>
              <Col md="12">
                <Card className="shadow mt-5 pl-4 pr-4 pb-2 pt-2">
                  {loading ? (
                    <Row>
                      <Col mod="12" className="text-center">
                        <Spinner />
                      </Col>
                    </Row>
                  ) : (
                    <></>
                  )}
                  {!loading && showForm ? (
                    <Row>
                      <Col mod="8">
                        <Row>
                          <Col md="12">
                            <img src={franchiseePicture} style={{ maxWidth: '80px' }} className="mt-3"></img>
                            <p style={{ fontSize: '1em' }} className="mt-1">
                              <strong>
                                {franchiseeName} {school?.name}
                              </strong>
                            </p>
                          </Col>
                          <Col md="12">
                            <h6 className="heading-small text-muted">
                              {step === 1 ? `Parent's Information` : ''}
                              {step === 2 ? `Children Information` : ''}
                              {step === 3 ? `Please review and confirm` : ''}
                            </h6>
                          </Col>
                          {step === 1 ? (
                            <>
                              {parents.map((parent, index: number) => (
                                <Col
                                  key={`parent-${index}`}
                                  md="12"
                                  style={{ paddingLeft: 0, marginLeft: 0 }}
                                  className="mb-2"
                                >
                                  <ParentForm
                                    index={index}
                                    firstName={parent.firstName}
                                    lastName={parent.lastName}
                                    email={parent.email}
                                    phone={parent.phone}
                                    setFirstName={setParentFirstName}
                                    setLastName={setParentLastName}
                                    setEmail={setParentEmail}
                                    setPhone={setParentPhone}
                                    emailLocked={!!parent.id}
                                    deleteParent={deleteParent}
                                  />
                                </Col>
                              ))}
                              <Col md="12">
                                <Button color="info" size="sm" className="mt-2 mb-2" onClick={addParent}>
                                  + Add another parent
                                </Button>
                              </Col>
                            </>
                          ) : (
                            <></>
                          )}
                          {step === 2 ? (
                            <Col md="12" style={{ paddingLeft: 0, marginLeft: 0 }} className="mb-2">
                              <ChildrenForm
                                index={0}
                                firstName={childFirstName}
                                lastName={childLastName}
                                birthday={childBirthday}
                                startDate={childStartDate}
                                schoolId={school?.id}
                                classId={classId}
                                classType={childClassType}
                                setFirstName={(_: number, value: string) => setChildFirstName(value)}
                                setLastName={(_: number, value: string) => setChildLastName(value)}
                                setBirthday={(_: number, value: string) => setChildBirthday(value)}
                                setStartDate={(_: number, value: string) => setChildStartDate(value)}
                                setClassType={(_: number, value: string) => setChildClassType(value)}
                                setClassId={(_: number, value: string) => setClassId(value)}
                                setClassTypeName={(_: number, value: string) => setClassTypeName(value)}
                                setPriceSheet={(_: number, value: any) => setPriceSheet(value)}
                              />
                            </Col>
                          ) : (
                            <></>
                          )}
                          {step === 3 ? (
                            <Col md="12" style={{ paddingLeft: 0, marginLeft: 0 }} className="mb-2">
                              <Card>
                                <CardHeader style={{ paddingBottom: 0 }}>
                                  <h6 className="heading-small text-muted">Parent Info</h6>
                                </CardHeader>
                                <CardBody>
                                  {parents.map((parent, index: number) => (
                                    <div key={`parent-confirm-${index}`}>
                                      {index > 0 ? <hr /> : <></>}
                                      <p style={{ ...styles.p, ...styles.bold }}>
                                        <strong>Name</strong>
                                      </p>
                                      <p style={styles.p}>{`${parent.firstName} ${parent.lastName}`}</p>
                                      <p style={{ ...styles.p, ...styles.bold }}>
                                        <strong>Email</strong>
                                      </p>
                                      <p style={styles.p}>{parent.email}</p>
                                      <p style={{ ...styles.p, ...styles.bold }}>
                                        <strong>Phone Number</strong>
                                      </p>
                                      <p style={styles.p}>{parent.phone}</p>
                                    </div>
                                  ))}
                                </CardBody>
                              </Card>
                              <Card>
                                <CardHeader style={{ paddingBottom: 0 }}>
                                  <h6 className="heading-small text-muted">Children Info</h6>
                                </CardHeader>
                                <CardBody>
                                  <p style={{ ...styles.p, ...styles.bold }}>
                                    <strong>Name</strong>
                                  </p>
                                  <p style={styles.p}>{`${childFirstName} ${childLastName}`}</p>
                                  <p style={{ ...styles.p, ...styles.bold }}>
                                    <strong>Date of Birth</strong>
                                  </p>
                                  <p style={styles.p}>{childBirthday}</p>
                                  <p style={{ ...styles.p, ...styles.bold }}>
                                    <strong>Class Type</strong>
                                  </p>
                                  <p style={styles.p}>{classTypeName}</p>
                                </CardBody>
                              </Card>
                            </Col>
                          ) : (
                            <></>
                          )}
                        </Row>
                      </Col>
                      <Col md="4">
                        <Row>
                          <Col md="12">
                            <Progress
                              animated
                              className="mt-3 mb-0"
                              value={progressBar.find((p) => p.step === step)?.percent || 0}
                            />{' '}
                            <div style={{ textAlign: 'right', fontSize: '12px' }}>Enrollment Progress</div>
                            <div style={{ height: '150px' }} className="mt-2">
                              {!isLoaded ? (
                                <h1>Loading...</h1>
                              ) : (
                                <GoogleMap mapContainerClassName="map-container" center={center} zoom={15}>
                                  <Marker
                                    position={{
                                      lat: Number(school?.addresses?.[0]?.latitude || 0),
                                      lng: Number(school?.addresses?.[0]?.longitude || 0),
                                    }}
                                  />
                                </GoogleMap>
                              )}
                            </div>
                          </Col>
                          <Col md="12" className="mt-3">
                            <Card>
                              <CardHeader style={{ paddingBottom: 0 }}>
                                <h6 className="heading-small text-muted">Pricing</h6>
                              </CardHeader>
                              <CardBody>
                                <p style={{ ...styles.p, ...styles.bold }}>
                                  <strong>Registration Fee</strong>
                                </p>
                                <p style={styles.p}>
                                  <span
                                    style={{
                                      textDecoration: promoCodeObj?.regFee ? 'line-through' : 'none',
                                      fontSize: promoCodeObj?.regFee ? '12px' : '14px',
                                      marginRight: '5px',
                                    }}
                                  >
                                    {school?.registrationFee
                                      ? `$${Number(school?.registrationFee || 0).toFixed(2)}`
                                      : '-'}
                                  </span>
                                  <span>
                                    {promoCodeObj?.regFee && school?.registrationFee
                                      ? `$${Number(
                                          Number(school?.registrationFee) -
                                            Number(school?.registrationFee) * (Number(promoCodeObj?.regFee) / 100),
                                        ).toFixed(2)}`
                                      : ''}
                                  </span>
                                </p>
                                <p style={{ ...styles.p, ...styles.bold }}>
                                  <strong>Annual Supply Fee</strong>
                                </p>
                                <p style={styles.p}>
                                  {school?.supplyFee ? `$${Number(school?.supplyFee || 0).toFixed(2)}` : '-'}
                                </p>
                                {priceSheet?.tuition ? (
                                  <>
                                    <p style={{ ...styles.p, ...styles.bold }}>
                                      <strong>{`${paymentFrequency} Tuition`}</strong>
                                    </p>
                                    <p style={styles.p}>
                                      <span
                                        style={{
                                          textDecoration: promoCodeObj?.tuitionFee ? 'line-through' : 'none',
                                          fontSize: promoCodeObj?.tuitionFee ? '12px' : '14px',
                                          marginRight: '5px',
                                        }}
                                      >
                                        {priceSheet?.tuition ? `$${Number(priceSheet?.tuition || 0).toFixed(2)}` : '-'}
                                      </span>
                                      <span>
                                        {promoCodeObj?.tuitionFee && priceSheet?.tuition
                                          ? `$${Number(
                                              Number(priceSheet?.tuition) -
                                                Number(priceSheet?.tuition) * (Number(promoCodeObj?.tuitionFee) / 100),
                                            ).toFixed(2)}`
                                          : ''}
                                      </span>
                                    </p>
                                  </>
                                ) : (
                                  <></>
                                )}
                                {step === 3 ? (
                                  <>
                                    {promoCodeApplied ? (
                                      <>
                                        <p style={{ ...styles.p, ...styles.bold }}>
                                          <strong>PROMO CODE</strong>
                                        </p>
                                        <p style={styles.p}>
                                          {promoCode}{' '}
                                          <i
                                            className="far fa-trash-alt text-danger"
                                            style={{
                                              cursor: 'pointer',
                                            }}
                                            onClick={removePromoCode}
                                          ></i>
                                        </p>
                                      </>
                                    ) : (
                                      <>
                                        <hr />
                                        <FormGroup>
                                          <label className="form-control-label">PROMO CODE</label>
                                          <InputGroup>
                                            <Input
                                              className="form-control-alternative"
                                              placeholder="PROMO CODE"
                                              type="text"
                                              value={promoCode}
                                              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                            />
                                            <InputGroupAddon addonType="append">
                                              <Button color="success" onClick={applyPromoCode}>
                                                Apply
                                              </Button>
                                            </InputGroupAddon>
                                          </InputGroup>
                                        </FormGroup>
                                        {promoCodeError ? (
                                          <span style={{ color: 'red' }}>Invalid Promo Code</span>
                                        ) : (
                                          <></>
                                        )}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <></>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="6">
                            {step > 1 ? (
                              <Button
                                color="danger"
                                size="sm"
                                className="mt-2 mb-2"
                                onClick={() => setStep(step - 1)}
                                style={{ width: '100%' }}
                              >
                                <i className="fas fa-arrow-left"></i> Back
                              </Button>
                            ) : (
                              <></>
                            )}
                          </Col>
                          <Col md="6">
                            <Button
                              color="primary"
                              size="sm"
                              className="mt-2 mb-2"
                              onClick={() => {
                                if (step === 2 && !childStartDate) {
                                  return toast.warn('Start date is required.');
                                }
                                return setStep(step + 1);
                              }}
                              style={{ width: '100%' }}
                              disabled={!parents?.[0]?.firstName || !parents?.[0]?.lastName ? true : false}
                            >
                              {step <= 2 ? (
                                <>
                                  Next Step <i className="fas fa-arrow-right"></i>
                                </>
                              ) : (
                                <>Confirm</>
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col mod="12" className="text-center">
                        {formMessage}
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
            </Row>
          </Container>
          <Modal
            className="modal-dialog-centered"
            size="md"
            isOpen={modalCard}
            toggle={(_: any) => setModalCard(false)}
          >
            <ModalHeader>Registration Fee</ModalHeader>
            <ModalBody>
              <span>{`This registration fee is a one-time, non-refundable, fee of $${Number(
                registrationFee || 0,
              ).toFixed(2)} unless otherwise specified.`}</span>
            </ModalBody>
            {clientSecret && stripePromise ? (
              <ModalBody>
                <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                  <EnrollPaymentForm
                    amount={registrationFee}
                    onSettled={handlePaymentSettled}
                    onCancel={cancelPayment}
                  />
                </Elements>
              </ModalBody>
            ) : (
              <ModalFooter>
                {paymentError ? <span className="text-danger mr-3">{paymentError}</span> : null}
                {!stripePK ? (
                  <span className="text-danger mr-3">
                    Online payments are not set up for this school yet. Please contact the school.
                  </span>
                ) : null}
                <Button color="danger" onClick={cancelPayment}>
                  Cancel
                </Button>
                <Button color="primary" onClick={startPayment} disabled={loading || !stripePK}>
                  {loading ? 'Loading...' : 'Agree and pay'}
                </Button>
              </ModalFooter>
            )}
          </Modal>
        </>
      ) : (
        <SuccessPage />
      )}
    </>
  );
};

export default EnrollForm;
